import Question, { QuestionProps } from '@/modules/chatbot/components/Question';
import SingleSelectInput from '@/modules/chatbot/components/SingleSelectInput';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_EVENTS,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import findNextQuestionToAsk from '@/modules/core/chatbot/findNextQuestionToAsk';
import { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import UpdatedQuestion from '@/modules/core/data/types/CDE/UpdatedQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { ModChatbot } from '@/modules/core/data/types/ModChatbot';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { useTheme } from '@emotion/react';
import isEqual from 'lodash.isequal';
import React, {
  Dispatch,
  Fragment,
  SetStateAction,
} from 'react';

type Props = {
  questions: ChatbotQuestions;
  activeQuestion: ChatbotQuestion;
  context: ChatbotContext;
  setActiveQuestion: Dispatch<SetStateAction<ChatbotQuestion>>;
  setContext: Dispatch<SetStateAction<ChatbotContext>>;
  updatedQuestion: UpdatedQuestion;
  setUpdatedQuestion: Dispatch<SetStateAction<UpdatedQuestion>>;
  updatedQuestionAnimationDuration: number;
  logEvent: LogEvent;
  bubbleTimerDuration: number;
  allEducationalPrograms: AirtableRecord<EducationalProgram>[];
  allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[];
  allCampuses: AirtableRecord<Campus>[];
  allStudentSolutionsCategories: AirtableRecord<StudentSolutionsCategory>[];
  modChatbot: AirtableRecord<ModChatbot>;
}

/**
 * Question about the programs the user's interested in.
 *
 * Always displayed second, unless deactivated.
 *
 * Although it's "plural", it only allows selecting one program.
 * That's because we handled plural first but that used to make the UX more complicated and wasn't what our customers wanted (GEM, in particular).
 */
const ProgramsQuestion: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    questions,
    activeQuestion,
    context,
    setActiveQuestion,
    setContext,
    updatedQuestion,
    setUpdatedQuestion,
    updatedQuestionAnimationDuration,
    logEvent,
    bubbleTimerDuration,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allCampuses,
    allStudentSolutionsCategories,
    modChatbot,
  } = props;
  const {
    campusesQuestion,
    programsQuestion,
    programStepsQuestion,
  } = questions;
  const theme: CustomerTheme = useTheme();

  return (
    <Question
      question={programsQuestion}
      activeQuestion={activeQuestion}
      context={context}
      bubbleTimerDuration={bubbleTimerDuration}
    >
      {
        (props: QuestionProps): JSX.Element => {
          const {
            question,
          } = props;

          return (
            <Fragment>
              <div
                className={'message'}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: question.label,
                  }}
                />
              </div>
              <div
                className={'message user select-input'}
              >
                <SingleSelectInput
                  question={question}
                  selectedValue={context.educationalPrograms?.[0]}
                  onChange={(selectedChoice: EducationalProgram): void => {
                    const newSelectedChoices: EducationalProgram[] = [selectedChoice];

                    if (!isEqual(newSelectedChoices, context[question.ref])) {
                      const nextContext: ChatbotContext = {
                        ...context,
                        [question.ref]: newSelectedChoices,
                        [programStepsQuestion.ref]: undefined, // Forget about the selected programSteps, because we changed the selected program and they depend on it
                        [campusesQuestion.ref]: undefined, // Forget about the selected campuses, because they depend on the selected programSteps
                      };

                      // Display next question
                      setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutionsCategories, modChatbot, setContext));

                      setUpdatedQuestion(question);
                      setTimeout(() => {
                        setUpdatedQuestion(null);
                      }, updatedQuestionAnimationDuration);

                      logEvent(AMPLITUDE_EVENTS.CHATBOT_ANSWER, {
                        question: {
                          ref: question.ref,
                          inputType: 'select',
                          isMultiChoices: false,
                          rank: question.order,
                        },
                        selection: {
                          ref: selectedChoice.ref,
                        },
                        action: AMPLITUDE_ACTIONS.SELECT,
                      });
                    }
                  }}
                  theme={theme}
                />
              </div>
            </Fragment>
          );
        }
      }
    </Question>
  );
};

export default ProgramsQuestion;
